import React, { useCallback, useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import { Box } from "@mui/system";
import ReactECharts from "echarts-for-react";
import { EChartsOption } from "echarts";
import useStyles from "../styles";
import maskUnits from "../../../utils/maskUnits";
import { getColorsArray } from "../../../utils/getColorsPallet";

interface MyDatum {
  [key: string]: any;
  quantidade: number;
}

const formatSeries = (inputData: MyDatum[], indexBy: string, keys: string) => {
  const keyValues = Array.from(new Set(inputData.map((d) => d[keys])));
  const indexValues = Array.from(new Set(inputData.map((d) => d[indexBy])));

  const matrixData: [string, string, number][] = [];

  keyValues.forEach((key) => {
    indexValues.forEach((value) => {
      const match = inputData.find(
        (d) => d[keys] === key && d[indexBy] === value,
      );
      matrixData.push([
        String(value),
        String(key),
        match ? Number(match.quantidade) : 0,
      ]);
    });
  });

  return {
    xData: indexValues.map((v) => ({ value: String(v) })),
    yData: keyValues.map((v) => ({ value: String(v) })),
    data: matrixData,
  };
};

type HeatMapProps = {
  data: MyDatum[];
  id: string;
  indexBy: string;
  keys: string;
  units?: Unit;
  fractionDigits?: number;
  colors?: { scheme?: string };
};

const HeatMapChart: React.FC<HeatMapProps> = ({
  data,
  id,
  indexBy,
  keys,
  colors,
  units,
  fractionDigits,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  //  const chartRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const chartRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        const { width, height } = entry.contentRect;

        if (width > 0 && height > 0) {
          setBoxWidth(width);
          setBoxHeight(height);
        }
      });

      resizeObserver.observe(node);

      (node as any)._observer = resizeObserver;
    }
  }, []);

  const { xData, yData, matrixData, minValue, maxValue } = useMemo(() => {
    if (!keys || !indexBy || !data) {
      return {
        xData: [],
        yData: [],
        matrixData: [],
        minValue: 0,
        maxValue: 0,
      };
    }

    const formatted = formatSeries(data, indexBy, keys);
    const values = formatted.data.map((d) => d[2]);
    const min = Math.min(...values);
    const max = Math.max(...values);

    return {
      xData: formatted.xData,
      yData: formatted.yData,
      matrixData: formatted.data,
      minValue: min,
      maxValue: max,
    };
  }, [data, indexBy, keys]);

  const chartOption: EChartsOption = useMemo(() => {
    let colorPalette = getColorsArray(5, colors, theme.palette.mode === "dark");
    colorPalette = [...colorPalette, "#f1f1f1"];

    const showLabel =
      boxWidth && boxHeight
        ? boxWidth / xData.length > 10 && boxHeight / yData.length > 10
        : false;

    return {
      animation: true,
      tooltip: {
        position: "top",
        backgroundColor: theme.palette.background.default,
        textStyle: { color: theme.palette.text.primary },
        confine: true,
        formatter: (params: any) => {
          const [xVal, yVal, value] = params.value;
          return `
            <div style="padding: 4px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${yVal}</div>
              <div style="color: ${theme.palette.text.secondary};">${xVal}</div>
              <div style="margin-top: 4px; font-size: 14px; font-weight: 600;">
                ${maskUnits(value, units, fractionDigits)}
              </div>
            </div>
          `;
        },
      },
      grid: {
        top: 0,
        right: 15,
        bottom: 65,
        left: 15,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        position: "top",
        data: xData.map((d) => d.value),
        axisLabel: {
          fontSize: 10,
          color: theme.palette.text.primary,
          // rotate: xData.length > 10 ? 45 : 0,
          hideOverlap: true,
        },
        splitArea: {
          show: true,
        },
      },
      yAxis: {
        type: "category",
        data: yData.map((d) => d.value),
        axisLabel: {
          fontSize: 10,
          color: theme.palette.text.primary,
          formatter: (value: string) =>
            value.length > 15 ? value.slice(0, 15) + "..." : value,
          hideOverlap: false,
          interval: 0,
        },
        splitArea: {
          show: true,
        },
      },
      visualMap: {
        type: "continuous",
        min: minValue,
        max: maxValue,
        calculable: true,
        orient: "horizontal",
        left: "center",
        inRange: {
          color: [...colorPalette].reverse(),
        },
        textStyle: {
          color: theme.palette.text.primary,
        },
        itemHeight: Math.max(100, Math.min((boxWidth ?? 400) - 100, 400)),
        itemWidth: 16,
        formatter: (value: any) => maskUnits(value, units, 0),
      },
      series: [
        {
          name: "HeatMap",
          type: "heatmap",
          data: matrixData,
          label: {
            show: showLabel,
            fontSize: 10,
            fontWeight: "bold",
            formatter: (params: any) =>
              maskUnits(params.value[2], units, fractionDigits),
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
          itemStyle: {
            // borderRadius: 4,
            borderColor: theme.palette.background.paper,
            // borderWidth: 2,
          },
        },
      ],
    };
  }, [
    xData,
    yData,
    matrixData,
    minValue,
    maxValue,
    boxWidth,
    boxHeight,
    colors,
    theme,
    units,
    fractionDigits,
  ]);

  const isReady = boxHeight && boxWidth && matrixData.length > 0;

  // Use matrixData check but remove isReady check for container
  return matrixData.length === 0 ? undefined : (
    <Box
      sx={{
        ...styles.boxContainer,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          ...styles.boxContainerSizeChart,
          overflow: "hidden",
          minWidth: 0,
          width: "100%",
          flexGrow: 1,
          height: "100%",
          position: "relative",
        }}
        ref={chartRef}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <ReactECharts
            key={`heatmap-${id}`}
            option={chartOption}
            style={{ height: "100%", width: "100%" }}
            notMerge={true}
            lazyUpdate={true}
            autoResize={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HeatMapChart;
